import React, { useEffect, useState } from 'react';
import { Modal } from 'reactstrap';
import { FormFeedback } from 'reactstrap';
import {
	useAddToPlayListMutation,
	useSubmitPersonalPlayListMutation,
	useAddToStudioPlayListMutation,
	useCreateStudioPlayListMutation
} from '../../../service';
import './add_to_playlist.css';
import { userInfoAtom } from '../../../data/atoms/utilsAtoms';
import { useAtomValue } from 'jotai';
import axios from 'axios';
import Toast from '../../../utils/toastManager';
import ClearIcon from '@mui/icons-material/Clear';
import { Users } from '@/constants/Users';

const DUPLICATE_RESPONSE = /already exist|already in/i;
const getVideoId = video => video?._id?.toString?.() || video?.value?.toString?.() || video?.id?.toString?.();

const addToPlayLists = async (request, playLists) => {
	const { data, error } = await request({ selectedVideosArray: playLists });
	return {
		names: playLists.map(el => el.name),
		failed: Boolean(error) || DUPLICATE_RESPONSE.test(data?.message || ''),
		message: error?.data?.message || data?.message
	};
};

function AddToPlayListModal({ modal, setModal, activeObject, viewData }) {
	const userData = useAtomValue(userInfoAtom);

	const [counter, setCounter] = useState(0);
	const [studioPlayList, setStudioPlayList] = useState([]);
	const [reqPersonal] = useSubmitPersonalPlayListMutation();
	const [reqAddToPlayList] = useAddToPlayListMutation();
	const [reqAddToStudioPlayList] = useAddToStudioPlayListMutation();
	const [reqCreate] = useCreateStudioPlayListMutation();
	const [inputVisibled, setInputVisibled] = useState(false);
	const [studioInputVisible, setStudioInputVisibled] = useState(false);
	const [playListName, setPlayListName] = useState('');
	const [studioPlaylistName, setStudioPlaylistName] = useState('');
	const [error, setError] = useState('');

	const [allPersonalList, setAllPersonalList] = useState([]);
	const [selectedPlayList, setSelectedPlayList] = useState([]);

	useEffect(() => {
		if (modal) {
			setSelectedPlayList([]);
			setInputVisibled(false);
			setStudioInputVisibled(false);
			setPlayListName('');
			setStudioPlaylistName('');
			setError('');
		}
	}, [modal]);

	useEffect(() => {
		const fetchPersonalPlaylists = async () => {
			try {
				const userId = userData.user?.userId;
				const response = await axios.get(`${import.meta.env.VITE_APP_API_URL}/personalvideo/fetch/all/${userId}`, {
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${userData?.token}`
					}
				});
				if (response.status === 200 && Array.isArray(response?.data?.data)) {
					const activeVideoId = getVideoId(activeObject);
					const formattedList = response.data?.data?.map(el => ({
						label: el.playlistName,
						value: el.playlistId,
						videos: el.videos
					}));

					const filteredList = formattedList.filter(el => !el.videos?.some(v => getVideoId(v) === activeVideoId));
					setAllPersonalList(filteredList);
				}
			} catch {
				Toast.error('Error fetching personal playlists');
			}
		};
		if (userData?.user?.userId && modal) {
			fetchPersonalPlaylists();
		}
	}, [userData, activeObject, counter, modal]);

	useEffect(() => {
		const fetchStudioPlaylists = async () => {
			try {
				const response = await axios.get(`${import.meta.env.VITE_APP_API_URL}/studio-playlist/all`, {
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${userData?.token}`
					}
				});

				if (
					response.status === 200 &&
					Array.isArray(response?.data?.data?.data) &&
					response?.data?.data?.data.length > 0
				) {
					const activeVideoId = getVideoId(activeObject);
					const filteredPlaylists = response.data.data.data.filter(
						playlist => !playlist.videos?.some(v => getVideoId(v) === activeVideoId)
					);

					const mappedPlaylists = filteredPlaylists.map(playlist => ({
						label: playlist.name,
						value: playlist._id,
						isStudio: true
					}));
					setStudioPlayList(mappedPlaylists);
				} else {
					setStudioPlayList([]);
				}
			} catch {
				Toast.error('Error fetching studio playlists');
			}
		};
		const isStudio = userData?.user?.userType === Users.studioOwner || userData?.user?.userType === Users.subUser;
		if (isStudio && modal) {
			fetchStudioPlaylists();
		}
	}, [userData, activeObject, counter, modal]);

	const handleCheckPlayList = (e, fld) => {
		if (e.target.checked) {
			setSelectedPlayList(prevSelected => [
				...prevSelected,
				{
					name: fld?.label,
					object_id: fld?.value,
					video_object: activeObject,
					isStudio: fld?.isStudio
				}
			]);
		} else {
			const updatedPlayList = selectedPlayList.filter(el => el?.object_id !== fld?.value);
			setSelectedPlayList(updatedPlayList);
		}
	};

	const handleChange = value => {
		setPlayListName(value);
		setError('');
	};
	const handleStudioChange = value => {
		setStudioPlaylistName(value);
		setError('');
	};

	const finishSave = () => {
		setModal(false);
		setInputVisibled(false);
		setStudioInputVisibled(false);
		setSelectedPlayList([]);
		setPlayListName('');
		setStudioPlaylistName('');
		setCounter(prevCounter => prevCounter + 1);
	};

	// Results are read from each request, not from the mutation hooks: a hook keeps its last success until it runs again.
	const handleAddResults = results => {
		const added = results.filter(result => !result.failed);
		const failed = results.filter(result => result.failed);
		const failureMessage = [...new Set(failed.map(result => result.message || 'Something went wrong'))].join(' ');

		if (failed.length === 0) {
			Toast.success(added[0]?.message || 'Added to playlist successfully');
			finishSave();
		} else if (added.length === 0) {
			Toast.error(failureMessage);
		} else {
			const addedNames = added.flatMap(result => result.names).map(name => `"${name}"`);
			Toast.error(`Added to ${addedNames.join(', ')}. ${failureMessage}`);
			finishSave();
		}
	};

	const handleCreateResult = ({ error: createError }) => {
		if (createError) {
			Toast.error('Something went wrong');
			return;
		}
		Toast.success('Created and added playlist successfully');
		finishSave();
	};

	const handleSubmit = async e => {
		e.preventDefault();
		if (selectedPlayList && Array.isArray(selectedPlayList) && selectedPlayList.length > 0) {
			const personalSelected = selectedPlayList.filter(el => !el.isStudio);
			const studioSelected = selectedPlayList.filter(el => el.isStudio);
			const requests = [];

			if (personalSelected.length > 0) {
				requests.push(addToPlayLists(reqAddToPlayList, personalSelected));
			}
			if (studioSelected.length > 0) {
				requests.push(addToPlayLists(reqAddToStudioPlayList, studioSelected));
			}
			handleAddResults(await Promise.all(requests));
		} else if (inputVisibled && playListName) {
			const result = await reqPersonal({
				name: playListName,
				description: '',
				videos: activeObject ? [activeObject] : [],
				userId: userData.user?.userId
			});
			handleCreateResult(result);
		} else if (studioInputVisible && studioPlaylistName) {
			const requestBody = {
				name: studioPlaylistName,
				description: '',
				videoIdsToAdd: activeObject ? [activeObject] : []
			};
			handleCreateResult(await reqCreate(requestBody));
		} else if (inputVisibled && !playListName) {
			setError('Name is required');
		} else if (studioInputVisible && !studioPlaylistName) {
			setError('Name is required');
		}
	};

	const cancelFuc = () => {
		setInputVisibled(false);
		setStudioInputVisibled(false);
		setPlayListName('');
		setStudioPlaylistName('');
		setError('');
		setSelectedPlayList([]);
		setModal(false);
	};

	const isStudioUser = userData?.user?.userType === Users.studioOwner || userData?.user?.userType === Users.subUser;

	return (
		<Modal centered isOpen={modal} toggle={cancelFuc}>
			<form>
				<div className="play__listing">
					<div className="play_list_heading">
						<div className="play_heading">Add to Playlist</div>
						<div className="close_icon_container" onClick={cancelFuc}>
							<ClearIcon size={32} style={{ color: 'white' }} />
						</div>
					</div>
					<div className="list_of_play">
						{allPersonalList.length > 0 ? <div className="play_heading_modal">Personal Playlists</div> : null}

						<div className="play_l">
							{allPersonalList &&
								Array.isArray(allPersonalList) &&
								allPersonalList?.length > 0 &&
								allPersonalList?.map((el, i) => {
									return (
										<label className="control_play control--checkbox" htmlFor={el?.value} key={i}>
											{el?.label}
											<input
												type="checkbox"
												name={el?.label}
												id={el?.value}
												value={el?.value}
												checked={selectedPlayList.some(item => item.object_id === el?.value)}
												onChange={e => handleCheckPlayList(e, el)}
											/>
											<div className="control__indicator"></div>
										</label>
									);
								})}
						</div>

						{isStudioUser && studioPlayList.length > 0 && (
							<>
								<div className="play_heading_modal">Studio Playlists</div>
								<div className="play_l">
									{studioPlayList &&
										Array.isArray(studioPlayList) &&
										studioPlayList?.length > 0 &&
										studioPlayList?.map((el, i) => {
											return (
												<label className="control_play control--checkbox" htmlFor={el?.value} key={i}>
													{el?.label}
													<input
														type="checkbox"
														name={el?.label}
														id={el?.value}
														value={el?.value}
														checked={selectedPlayList.some(item => item.object_id === el?.value)}
														onChange={e => handleCheckPlayList(e, el)}
													/>
													<div className="control__indicator"></div>
												</label>
											);
										})}
								</div>
							</>
						)}

						{isStudioUser ? (
							<>
								<div className="creat_n_play_studio" style={{ cursor: 'pointer' }}>
									<div className="play_heading">Create New </div>
									<div className="play_list_name_section">
										<div className="personal" onClick={() => setInputVisibled(!inputVisibled)}>
											<div className="play_heading_personal">Personal Playlist</div>
											<div className="close_icon"></div>
										</div>
										<div className={`add_name_play ${inputVisibled ? 'd-block' : 'd-none'}`}>
											<input
												type="text"
												placeholder="Enter name of new personal playlist"
												value={playListName}
												onChange={e => handleChange(e.target.value)}
											/>
											{error && <FormFeedback>{error}</FormFeedback>}
										</div>

										<div className="studio" onClick={() => setStudioInputVisibled(!studioInputVisible)}>
											<div className="play_heading_studio">Studio Playlist</div>
											<div className="close_icon"></div>
										</div>

										<div className={`add_name_play ${studioInputVisible ? 'd-block' : 'd-none'}`}>
											<input
												type="text"
												placeholder="Enter name of new studio playlist"
												value={studioPlaylistName}
												onChange={e => handleStudioChange(e.target.value)}
											/>
											{error && <FormFeedback>{error}</FormFeedback>}
										</div>
									</div>
								</div>
							</>
						) : (
							<>
								<div
									className="creat_n_play"
									style={{ cursor: 'pointer' }}
									onClick={() => setInputVisibled(!inputVisibled)}
								>
									<div className="play_heading">Create New Personal Playlist</div>
									<div className="close_icon"></div>
								</div>
								<div className={`add_name_play ${inputVisibled ? 'd-block' : 'd-none'}`}>
									<input
										type="text"
										placeholder="Enter name of new playlist"
										value={playListName}
										onChange={e => handleChange(e.target.value)}
									/>
									{error && <FormFeedback>{error}</FormFeedback>}
								</div>
							</>
						)}
					</div>

					<button className="save_play_list" onClick={e => handleSubmit(e)}>
						Save
					</button>
				</div>
			</form>
		</Modal>
	);
}

export default AddToPlayListModal;
